// src/api/api.js
import axios from 'axios';
import { getRequestPath, getUnauthorizedRecovery, shouldAttachAuthToken } from '../utils/authRequest';
import { clearClientAuthStorage } from '../utils/clearClientAuthStorage';
import { settleTransientNetworkError } from '../utils/transientNetworkRetry';
import { safeLocalStorage } from '../utils/safeLocalStorage';

// Global cap so a stalled socket cannot hang the SPA. Search, credit-info and
// the observations counter fail sooner and every read is retried once.
// Login/logout use a shorter budget; uploads can override.
export const DEFAULT_REQUEST_TIMEOUT_MS = 60000;
export const READ_REQUEST_TIMEOUT_MS = 20000;
export const AUTH_REQUEST_TIMEOUT_MS = 15000;
export const UPLOAD_REQUEST_TIMEOUT_MS = 180000;

function isReadMethod(method) {
  const normalized = String(method || 'get').toLowerCase();
  return normalized === 'get' || normalized === 'head' || normalized === 'options';
}

function usesShortReadTimeout(config) {
  if (!isReadMethod(config.method)) {
    return false;
  }

  if (config.timeout != null && config.timeout !== DEFAULT_REQUEST_TIMEOUT_MS) {
    return false;
  }

  const path = getRequestPath(config);
  return path.endsWith('/customers/search')
    || path.endsWith('/credit-info')
    || path.endsWith('/observations/count');
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: DEFAULT_REQUEST_TIMEOUT_MS,
});

function stripAuthorizationHeader(config) {
  if (!config.headers) {
    return;
  }

  if (typeof config.headers.delete === 'function') {
    config.headers.delete('Authorization');
    return;
  }

  delete config.headers.Authorization;
  delete config.headers.authorization;
}

// Interceptor para incluir el token
// Prioriza el token temporal de autogestión si existe, sino usa el token regular del vendedor
api.interceptors.request.use((config) => {
  if (!shouldAttachAuthToken(config)) {
    stripAuthorizationHeader(config);
    return config;
  }

  const isMobilePath = typeof window !== 'undefined' && window.location.pathname.startsWith('/m/');
  const tempToken = safeLocalStorage.getItem('temp_authToken');
  const regularToken = safeLocalStorage.getItem('authToken');

  // Evitar cruce de sesión: token temporal solo para rutas mobile
  const token = isMobilePath ? (tempToken || regularToken) : regularToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  if (usesShortReadTimeout(config)) {
    config.timeout = READ_REQUEST_TIMEOUT_MS;
  }

  return config;
});

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const settled = await settleTransientNetworkError(error, (config) => api.request(config));
    if (settled !== null) {
      return settled;
    }

    const recovery = getUnauthorizedRecovery({
      status: error.response?.status,
      config: error.config || {},
      currentPath: typeof window !== 'undefined' ? window.location.pathname : '',
    });

    if (recovery === 'mobile') {
      const errorType = error.response?.data?.message?.includes('expired')
        ? 'token_expired'
        : 'invalid_token';
      window.location.href = `/m/error/${errorType}`;
    } else if (recovery === 'desktop') {
      clearClientAuthStorage();
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

export default api;
